import {useEffect, useId, useRef, useState} from "react";
import {createPortal} from "react-dom";
import {X} from "lucide-react";
import {compareMoney, normalizeMoney} from "../../../shared/finance/decimal";
import {useTranslation} from "../../../shared/i18n";
import "../refunds.css";

const EMPTY_VALUES = Object.freeze({
    paymentBillId: "",
    refundType: "full",
    amount: "",
    reason: "",
});

export const RefundRequestDialog = ({
    open,
    pending,
    retryRecord,
    error,
    onCancel,
    onConfirm,
    onRetry,
}) => {
    const {t} = useTranslation();
    const titleId = useId();
    const descriptionId = useId();
    const [values, setValues] = useState({...EMPTY_VALUES});
    const [validationError, setValidationError] = useState("");
    const wasOpenRef = useRef(false);
    const firstInputRef = useRef(null);

    useEffect(() => {
        if (!open) {
            wasOpenRef.current = false;
            return undefined;
        }
        if (!wasOpenRef.current) {
            wasOpenRef.current = true;
            setValues({...EMPTY_VALUES});
            setValidationError("");
        }
        const frame = window.requestAnimationFrame(() => firstInputRef.current?.focus());
        return () => window.cancelAnimationFrame(frame);
    }, [open]);

    useEffect(() => {
        if (!open) return undefined;
        const handleKeyDown = (event) => {
            if (event.key === "Escape" && !pending) onCancel();
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [onCancel, open, pending]);

    if (!open) return null;

    const locked = pending || Boolean(retryRecord);
    const setValue = (name, value) => {
        setValues((current) => ({...current, [name]: value}));
        setValidationError("");
    };

    const submit = (event) => {
        event.preventDefault();
        if (pending) return;
        if (retryRecord) {
            onRetry();
            return;
        }

        const paymentBillId = values.paymentBillId.trim();
        const reason = values.reason.trim();
        if (!paymentBillId) {
            setValidationError(t("money.refunds.dialog.billRequired"));
            return;
        }
        if (!reason) {
            setValidationError(t("money.refunds.dialog.reasonRequired"));
            return;
        }

        let amount = null;
        if (values.refundType === "partial") {
            try {
                amount = normalizeMoney(values.amount);
                if (compareMoney(amount, "0.00") <= 0) {
                    setValidationError(t("money.refunds.dialog.amountPositive"));
                    return;
                }
            } catch (amountError) {
                setValidationError(amountError.message);
                return;
            }
        }

        onConfirm({
            paymentBillId,
            payload: {
                refund_type: values.refundType,
                amount,
                reason,
            },
        });
    };

    const alertMessage = validationError || error?.message;
    return createPortal(
        <div
            className="finance-dialog-backdrop"
            onMouseDown={(event) => {
                if (event.target === event.currentTarget && !pending) onCancel();
            }}
        >
            <section
                className="finance-dialog refund-request-dialog"
                role="dialog"
                aria-modal="true"
                aria-busy={pending}
                aria-labelledby={titleId}
                aria-describedby={descriptionId}
            >
                <form onSubmit={submit} noValidate>
                    <header className="finance-dialog-header">
                        <div>
                            <p className="finance-dialog-entity">{t("money.refunds.dialog.eyebrow")}</p>
                            <h2 id={titleId}>{t("money.refunds.dialog.title")}</h2>
                        </div>
                        <button
                            className="finance-icon-button"
                            type="button"
                            aria-label={t("common.confirmation.close")}
                            title={t("common.confirmation.close")}
                            disabled={pending}
                            onClick={onCancel}
                        >
                            <X aria-hidden="true" size={18} />
                        </button>
                    </header>
                    <p id={descriptionId}>
                        {t("money.refunds.dialog.description")}
                    </p>
                    <div className="finance-dialog-fields">
                        <label>
                            <span>{t("money.refunds.dialog.billId")}</span>
                            <input
                                ref={firstInputRef}
                                type="text"
                                value={values.paymentBillId}
                                disabled={locked}
                                onChange={(event) => setValue("paymentBillId", event.target.value)}
                            />
                        </label>
                        <label>
                            <span>{t("money.refunds.dialog.type")}</span>
                            <select
                                aria-label={t("money.refunds.dialog.type")}
                                value={values.refundType}
                                disabled={locked}
                                onChange={(event) => setValue("refundType", event.target.value)}
                            >
                                <option value="full">{t("money.refunds.dialog.full")}</option>
                                <option value="partial">{t("money.refunds.dialog.partial")}</option>
                            </select>
                        </label>
                        {values.refundType === "partial" ? (
                            <label>
                                <span>{t("money.refunds.dialog.partialAmount")}</span>
                                <input
                                    type="text"
                                    inputMode="decimal"
                                    value={values.amount}
                                    disabled={locked}
                                    onChange={(event) => setValue("amount", event.target.value)}
                                />
                            </label>
                        ) : null}
                        <label>
                            <span>{t("money.refunds.dialog.reason")}</span>
                            <textarea
                                value={values.reason}
                                disabled={locked}
                                onChange={(event) => setValue("reason", event.target.value)}
                            />
                        </label>
                    </div>
                    {alertMessage ? (
                        <div className="finance-dialog-error" role="alert">
                            <p>{alertMessage}</p>
                        </div>
                    ) : null}
                    {retryRecord ? (
                        <p className="refund-retry-note">
                            {t("money.refunds.dialog.retryNote", {key: retryRecord.idempotencyKey})}
                        </p>
                    ) : null}
                    <footer className="finance-dialog-footer">
                        <button
                            type="button"
                            className="admin-button admin-button-secondary"
                            disabled={pending}
                            onClick={onCancel}
                        >
                            {t("common.confirmation.cancel")}
                        </button>
                        <button
                            type="submit"
                            className="admin-button admin-button-primary"
                            disabled={pending}
                        >
                            {pending
                                ? t("money.refunds.dialog.pending")
                                : retryRecord
                                    ? t("money.refunds.dialog.retry")
                                    : t("money.refunds.dialog.submit")}
                        </button>
                    </footer>
                </form>
            </section>
        </div>,
        document.body,
    );
};
